<script setup>
/**
 * 용신라이트 표지 캘린더 장식 컴포넌트
 * - Figma node: 580:3295 (표지 캘린더 영역)
 * - 기존 단일 이미지(ys_light_calendar.png)를 개별 요소 배치 + 애니메이션으로 대체
 */
import imgOakTree from '@manseImages/yongsin-light/icon-oak-tree.png'
import imgGreekSalad from '@manseImages/yongsin-light/icon-greek-salad.png'
import imgAlarmClock from '@manseImages/yongsin-light/icon-alarm-clock.png'
import imgFire from '@manseImages/yongsin-light/icon-fire.png'
import imgWater from '@manseImages/yongsin-light/icon-water.png'
import imgStar from '@manseImages/yongsin-light/icon-star.png'
import imgRunner from '@manseImages/yongsin-light/icon-track-and-field.png'
import imgDiamond from '@manseImages/yongsin-light/icon-diamond.png'
import imgTwistedArrow from '@manseImages/yongsin-light/icon-twisted-arrow.svg?url'
import imgTwistedArrowRight from '@manseImages/yongsin-light/icon-twisted-arrow-right.svg?url'
import imgWave from '@manseImages/yongsin-light/icon-wave.svg?url'

const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const weeks = [
  [null, null, null, 1, 2, 3, 4],
  [5, 6, 7, 8, 9, 10, 11],
  [12, 13, 14, 15, 16, 17, 18],
  [19, 20, 21, 22, 23, 24, 25],
  [26, 27, 28, 29, 30, 31, null],
]

const icons = [
  { src: imgStar, cls: 'star', alt: 'Star of Bethlehem' },
  { src: imgOakTree, cls: 'tree', alt: 'Oak Tree' },
  { src: imgGreekSalad, cls: 'salad', alt: 'Greek Salad' },
  { src: imgAlarmClock, cls: 'clock', alt: 'Alarm Clock' },
  { src: imgFire, cls: 'fire', alt: 'Fire' },
  { src: imgWater, cls: 'water', alt: 'Water' },
  { src: imgDiamond, cls: 'diamond', alt: 'Diamond' },
  { src: imgRunner, cls: 'runner', alt: 'Track and Field' },
]

const hashtags = [
  { text: '#내 용신', cls: 'yongsin' },
  { text: '#내 희신', cls: 'heeshin' },
  { text: '#오늘 점심', cls: 'lunch' },
  { text: '#샐러드', cls: 'salad' },
  { text: '#러닝', cls: 'running' },
  { text: '#약속', cls: 'promise' },
  { text: '#데이트', cls: 'date' },
]
</script>

<template>
  <div class="yl-decor">
    <!-- ── Calendar Grid ── -->
    <div class="yl-decor__calendar">
      <div class="yl-decor__days">
        <span v-for="(day, i) in days" :key="i" class="yl-decor__day">{{ day }}</span>
      </div>
      <div class="yl-decor__grid">
        <template v-for="(week, wi) in weeks" :key="wi">
          <div
            v-for="(date, di) in week"
            :key="`${wi}-${di}`"
            class="yl-decor__cell"
            :style="{ '--cell-delay': `${(wi * 7 + di) * 0.02}s` }"
          >
            <span v-if="date" class="yl-decor__date">{{ date }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- ── Post-it: Yellow ── -->
    <div class="yl-decor__postit yl-decor__postit--yellow">
      <div class="yl-decor__postit-gloss"></div>
      <div class="yl-decor__postit-text">
        <p>나를</p>
        <p>보좌해주는 기운</p>
      </div>
    </div>

    <!-- ── Post-it: Pink ── -->
    <div class="yl-decor__postit yl-decor__postit--pink">
      <div class="yl-decor__postit-gloss"></div>
      <div class="yl-decor__postit-text">
        <p>나에게</p>
        <p>힘을 주는 기운</p>
      </div>
    </div>

    <!-- ── Emoji Icons ── -->
    <img
      v-for="icon in icons"
      :key="icon.cls"
      :src="icon.src"
      :alt="icon.alt"
      :class="['yl-decor__icon', `yl-decor__icon--${icon.cls}`]"
    />

    <!-- ── Decorative Stars ── -->
    <span class="yl-decor__sparkle yl-decor__sparkle--1">✦</span>
    <span class="yl-decor__sparkle yl-decor__sparkle--2">✦</span>

    <!-- ── 물결 (시계 왼쪽) ── -->
    <img :src="imgWave" alt="" class="yl-decor__deco yl-decor__deco--wave1" />
    <img :src="imgWave" alt="" class="yl-decor__deco yl-decor__deco--wave2" />

    <!-- ── 꼬인 화살표 (다이아몬드 오른쪽) ── -->
    <img :src="imgTwistedArrow" alt="" class="yl-decor__deco yl-decor__deco--arrow1" />
    <!-- ── 꼬인 화살표 오른쪽 (러닝 왼쪽) ── -->
    <img :src="imgTwistedArrowRight" alt="" class="yl-decor__deco yl-decor__deco--arrow2" />

    <!-- ── Hashtags ── -->
    <span
      v-for="tag in hashtags"
      :key="tag.cls"
      :class="['yl-decor__tag', `yl-decor__tag--${tag.cls}`]"
    >{{ tag.text }}</span>
  </div>
</template>

<style lang="scss" scoped>
/* ═══════════════════════════════════════════
   Reference frame: 600 × 520 (Figma origin offset y=296)
   All % positions computed from this frame
   ═══════════════════════════════════════════ */

// ── Keyframes ──
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes popIn {
  0%   { opacity: 0; transform: scale(0.3); }
  60%  { opacity: 1; transform: scale(1.08); }
  100% { transform: scale(1); }
}
@keyframes floatY {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
@keyframes sway {
  0%, 100% { transform: rotate(var(--base-rot, 0deg)); }
  50%      { transform: rotate(calc(var(--base-rot, 0deg) + 3deg)); }
}
@keyframes twinkle {
  0%, 100% { opacity: 0.4; transform: scale(0.8); }
  50%      { opacity: 1; transform: scale(1.1); }
}
@keyframes slideInPostit {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes gridFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

// ── Container (container query로 모든 자식 비율 유지) ──
.yl-decor {
  position: relative;
  width: 100%;
  aspect-ratio: 600 / 520;
  overflow: visible;
  container-type: inline-size;
}

// ── Calendar ──
.yl-decor__calendar {
  position: absolute;
  left: 7.37%;
  top: 1.92%;
  width: 85.97%;
  animation: fadeInUp 0.6s ease-out both;
}

.yl-decor__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 2px;
}

.yl-decor__day {
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-weight: 700;
  font-size: 2.5cqw;
  line-height: 1.3;
  color: #5d81bf;
  padding-bottom: 0.7cqw;
}

.yl-decor__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(5, 1fr);
  border: 2px solid #fff;
}

.yl-decor__cell {
  aspect-ratio: 73.685 / 90.019;
  border: 1px solid #fff;
  padding: 0.7cqw;
  box-sizing: border-box;
  animation: gridFadeIn 0.3s ease-out both;
  animation-delay: var(--cell-delay, 0s);
}

.yl-decor__date {
  font-family: Pretendard, sans-serif;
  font-weight: 700;
  font-size: 2.5cqw;
  line-height: 1;
  color: #5d81bf;
}

// ── Post-it Notes ──
.yl-decor__postit {
  position: absolute;
  border-radius: 2px;
  box-shadow: 2px 3px 10px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  z-index: 2;
}

.yl-decor__postit--yellow {
  --base-rot: -1.94deg;
  left: 22.4%;
  top: 31.6%;
  width: 23.3%;
  aspect-ratio: 135.52 / 131.785;
  background: linear-gradient(145deg, #fef0c8, #fce6b4);
  animation: slideInPostit 0.6s ease-out 0.6s both;
}

.yl-decor__postit--pink {
  --base-rot: 2.06deg;
  left: 51.4%;
  top: 44.4%;
  width: 31%;
  aspect-ratio: 177.7 / 161.62;
  background: linear-gradient(145deg, #fce0e2, #f9d4d7);
  animation: slideInPostit 0.6s ease-out 0.8s both;
}

.yl-decor__postit-gloss {
  position: absolute;
  top: 0;
  left: 10%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    74deg,
    rgba(245, 245, 245, 0) 0%,
    rgba(255, 255, 255, 0.35) 48%,
    rgba(255, 255, 255, 0.35) 52%,
    rgba(245, 245, 245, 0) 100%
  );
  opacity: 0.4;
  pointer-events: none;
  z-index: 0;
}

.yl-decor__postit-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'GyeonggiTitle', Pretendard, sans-serif;
  font-weight: 500;
  color: #333;
  text-align: center;
  line-height: 1.4;
  z-index: 1;

  p {
    margin: 0;
  }
}

.yl-decor__postit--yellow .yl-decor__postit-text {
  font-size: 2.83cqw;
}

.yl-decor__postit--pink .yl-decor__postit-text {
  font-size: 3.33cqw;
}

// ── Emoji Icons ──
.yl-decor__icon {
  position: absolute;
  object-fit: contain;
  pointer-events: none;
  z-index: 3;
  animation: popIn 0.5s ease-out both;
}

// Icon positions & sizes (Figma coords → %)
.yl-decor__icon--star {
  left: 16.4%;
  top: 6.9%;
  width: 13.1%;
  transform: rotate(13.63deg);
  animation-delay: 0.3s;
  --float-delay: 0s;
}

.yl-decor__icon--tree {
  left: 3.1%;
  top: 28.2%;
  width: 10.7%;
  transform: rotate(-4.3deg);
  animation-delay: 0.5s;
  --float-delay: 0.5s;
}

.yl-decor__icon--salad {
  left: 46.5%;
  top: 12.8%;
  width: 13.8%;
  transform: rotate(2.21deg);
  animation-delay: 0.4s;
  --float-delay: 0.3s;
}

.yl-decor__icon--clock {
  left: 14.4%;
  top: 62.3%;
  width: 12.2%;
  transform: rotate(-4.58deg);
  animation-delay: 0.7s;
  --float-delay: 0.8s;
}

.yl-decor__icon--fire {
  left: 62.3%;
  top: 86.3%;
  width: 10.9%;
  transform: rotate(5.53deg);
  animation-delay: 0.9s;
  --float-delay: 1.2s;
}

.yl-decor__icon--water {
  left: 87.6%;
  top: 70.4%;
  width: 11.5%;
  transform: rotate(11.3deg);
  animation-delay: 0.8s;
  --float-delay: 0.6s;
}

.yl-decor__icon--diamond {
  left: 8%;
  top: 80.1%;
  width: 11.3%;
  transform: rotate(-9.68deg);
  animation-delay: 1s;
  --float-delay: 1s;
}

.yl-decor__icon--runner {
  left: 70.3%;
  top: 27.5%;
  width: 10%;
  animation-delay: 0.6s;
  --float-delay: 0.4s;
}

// Continuous float after entrance
.yl-decor__icon {
  &.is-entered {
    animation: floatY 3s ease-in-out infinite;
    animation-delay: var(--float-delay, 0s);
  }
}

// ── Decorative Stars ──
.yl-decor__sparkle {
  position: absolute;
  font-size: 2.3cqw;
  color: #5d81bf;
  z-index: 4;
  animation: twinkle 2s ease-in-out infinite;
  pointer-events: none;
}

.yl-decor__sparkle--1 {
  left: 83.5%;
  top: 30.2%;
  transform: rotate(-178.94deg);
  animation-delay: 0s;
}

.yl-decor__sparkle--2 {
  left: 82.8%;
  top: 33.2%;
  transform: rotate(175.93deg);
  animation-delay: 0.5s;
}

// ── 장식 (물결 / 꼬인 화살표) ──
.yl-decor__deco {
  position: absolute;
  object-fit: contain;
  pointer-events: none;
  z-index: 4;
  opacity: 0;
  animation: fadeInUp 0.4s ease-out both;
}

// 물결 (시계 왼쪽)
.yl-decor__deco--wave1 {
  left: 11%;
  top: 65.2%;
  width: 4%;
  transform: rotate(11deg);
  animation-delay: 1.1s;
}

.yl-decor__deco--wave2 {
  left: 11%;
  top: 67.7%;
  width: 4%;
  transform: rotate(-8.3deg);
  animation-delay: 1.15s;
}

// 꼬인 화살표 (다이아몬드 오른쪽)
.yl-decor__deco--arrow1 {
  left: 19.6%;
  top: 82.5%;
  width: 4.5%;
  transform: rotate(34.7deg);
  animation-delay: 1.2s;
}

// 꼬인 화살표 오른쪽 (러닝 왼쪽)
.yl-decor__deco--arrow2 {
  left: 66.5%;
  top: 30.2%;
  width: 4.5%;
  animation-delay: 1.25s;
}

// ── Hashtags ──
.yl-decor__tag {
  position: absolute;
  font-family: 'GyeonggiTitle', Pretendard, sans-serif;
  font-weight: 300;
  font-size: 2.17cqw;
  line-height: 1;
  color: #575757;
  white-space: nowrap;
  z-index: 4;
  pointer-events: none;
  opacity: 0;
  animation: fadeInUp 0.4s ease-out both;
}

.yl-decor__tag--yongsin {
  left: 11.8%;
  top: 29%;
  animation-delay: 0.9s;
}

.yl-decor__tag--heeshin {
  left: 83.4%;
  top: 71.6%;
  animation-delay: 1.1s;
}

.yl-decor__tag--lunch {
  left: 58.8%;
  top: 12.9%;
  animation-delay: 0.8s;
}

.yl-decor__tag--salad {
  left: 58.8%;
  top: 16.2%;
  animation-delay: 0.85s;
}

.yl-decor__tag--running {
  left: 61.8%;
  top: 34%;
  animation-delay: 0.95s;
}

.yl-decor__tag--promise {
  left: 25.8%;
  top: 66.9%;
  animation-delay: 1s;
}

.yl-decor__tag--date {
  left: 25.8%;
  top: 70.4%;
  animation-delay: 1.05s;
}
</style>
